'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useCommerceSource,
  tx,
  PortalLink,
  type Order,
  type OrderLookup,
  type PaymentResult,
} from '@akademi/site-builder-runtime';
import { BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import { OrderView, OrderLookupForm } from './orderViews';
import { readPlacedOrder } from './orderHandoff';
import { BankTransferNotice, bankTransferOf } from './bankTransfer';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * "Thank you — here is your order", and whether it is paid for.
 *
 * <p>The order arrives from the checkout page through session storage rather
 * than through the URL; see `orderHandoff` for why an order number plus proofs
 * must never be in an address bar.
 *
 * <p>When storage has nothing — a refresh in a new tab, a bookmarked URL, a
 * browser with site data blocked — this does NOT show an error. The order was
 * placed; only the handoff was lost. It falls back to the same lookup form the
 * tracking page uses, so the shopper gets to their order in three fields
 * instead of a dead end.
 *
 * <h3>Payment, and the race this page is on the wrong side of</h3>
 *
 * <p>A shopper sent to 3-D Secure comes back here, and their browser almost
 * always arrives before the acquirer's webhook does. So an unpaid-looking order
 * on first paint is the normal case, not a failure, and saying "payment failed"
 * then would be wrong most of the time it was said. This polls a few times
 * before drawing any conclusion, and the conclusion it draws while still
 * waiting is "we are confirming", never "it did not work".
 *
 * <p>Polling stops. An indefinite poll on an order that will never be paid is a
 * page that hammers the backend for as long as a tab stays open — so after a
 * fixed number of attempts it offers the shopper a retry and leaves it to them.
 *
 * <p>Reading storage happens in an effect, not during render, so the server and
 * the first client paint agree. Doing it inline would hydrate-mismatch every
 * time, which React resolves by throwing the server's HTML away.
 */
export interface OrderConfirmationProps {
  title?: Localized;
  /** Shown under the title on a successful confirmation. */
  message?: Localized;
  /** Where "keep shopping" goes. */
  continueHref?: string;
}

/** Statuses that mean the money question is settled — stop asking. */
const SETTLED = new Set(['APPROVED', 'ALREADY_PAID', 'FAILED']);

/** How many times to ask before handing the decision back to the shopper. */
const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2500;

export function OrderConfirmation({
  title,
  message,
  continueHref = '/categories',
}: OrderConfirmationProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');

  const [order, setOrder] = useState<Order | null>(null);
  const [proof, setProof] = useState<OrderLookup | null>(null);
  const [payment, setPayment] = useState<PaymentResult | null>(null);
  const [checked, setChecked] = useState(false);
  const [polling, setPolling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    const placed = readPlacedOrder();
    if (placed) {
      setOrder(placed.order);
      setProof(placed.proof);
    }
    setChecked(true);
  }, []);

  const settled = payment != null && SETTLED.has(payment.status);
  // A transfer takes days, not seconds: there is nothing to poll for, and "we
  // could not confirm your payment" would be exactly the wrong thing to say.
  const transfer = bankTransferOf(order);

  useEffect(() => {
    if (!order || !proof || !commerce?.paymentStatus || settled || transfer) return;
    if (polls.current >= MAX_POLLS) {
      setPolling(false);
      return;
    }
    setPolling(true);
    let cancelled = false;
    const timer = window.setTimeout(
      () => {
        polls.current += 1;
        commerce
          .paymentStatus!(order.orderNumber, proof)
          .then((result) => {
            if (!cancelled && result) setPayment(result);
          })
          .catch(() => {
            // A failed poll is not a failed payment. Leave the state alone and
            // let the next attempt decide; the counter still advances, so this
            // cannot spin for ever either.
          });
      },
      // The first look is immediate — by the time a redirect has round-tripped
      // the webhook has often landed — and only the retries wait.
      polls.current === 0 ? 0 : POLL_INTERVAL_MS,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [order, proof, commerce, payment, settled, transfer]);

  const retry = useCallback(async () => {
    if (!order || !proof || !commerce?.payForOrder || retrying) return;
    setRetrying(true);
    try {
      const result = await commerce.payForOrder({
        orderNumber: order.orderNumber,
        ...proof,
        returnUrl: typeof window === 'undefined' ? undefined : window.location.pathname,
      });
      if (result.actionUrl) {
        window.location.assign(result.actionUrl);
        return;
      }
      setPayment(result);
      polls.current = 0;
    } catch {
      setPayment({
        orderNumber: order.orderNumber,
        status: 'FAILED',
        amount: order.total,
        currency: order.currency,
      });
    } finally {
      setRetrying(false);
    }
  }, [order, proof, commerce, retrying]);

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 820 }}>
        <h1 {...part('title')} style={titleStyle}>
          {order
            ? tx(title) || t('commerce.confirmation.title')
            : t('commerce.confirmation.lookupTitle')}
        </h1>

        {!checked && <p style={mutedTextStyle}>{t('commerce.confirmation.loading')}</p>}

        {checked && order && (
          <>
            <p {...part('message')} style={leadStyle}>
              {tx(message) || t('commerce.confirmation.message', { orderNumber: order.orderNumber })}
            </p>
            {/* The email is the shopper's own copy. Saying so here is the
                difference between a shopper who waits and one who rings up. */}
            <p style={mutedTextStyle}>{t('commerce.confirmation.emailed')}</p>

            {transfer ? (
              <BankTransferNotice order={order} transfer={transfer} />
            ) : (
              <PaymentNotice
                payment={payment}
                polling={polling && !settled}
                retrying={retrying}
                canRetry={Boolean(commerce?.payForOrder)}
                onRetry={() => void retry()}
              />
            )}

            <div style={{ margin: '24px 0' }}>
              <OrderView order={order} transferNotice={false} />
            </div>
            <PortalLink href={continueHref} style={buttonStyle}>
              {t('commerce.cart.continue')}
            </PortalLink>
          </>
        )}

        {checked && !order && (
          <>
            <p style={leadStyle}>{t('commerce.confirmation.lookupLead')}</p>
            <OrderLookupForm
              onFound={(found) => {
                setOrder(found);
                // A shopper who looked the order up has proved nothing this page
                // may reuse — the form holds the proofs, not this block — so no
                // payment polling follows. They can see their order; paying again
                // from here would need them to be sure it is unpaid, which the
                // order itself tells them.
              }}
            />
          </>
        )}
      </div>
    </BlockRoot>
  );
}

/**
 * One line about the money, in the register the state deserves.
 *
 * <p>Nothing at all while there is genuinely nothing to say: an order in a shop
 * with no gateway configured is complete as it stands, and inventing a payment
 * status for it would be worse than silence.
 */
function PaymentNotice({
  payment,
  polling,
  retrying,
  canRetry,
  onRetry,
}: {
  payment: PaymentResult | null;
  polling: boolean;
  retrying: boolean;
  canRetry: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');

  if (polling && (!payment || payment.status === 'PENDING')) {
    return (
      <p style={noticeStyle} role="status">
        {t('commerce.confirmation.paymentChecking')}
      </p>
    );
  }
  if (!payment) return null;

  if (payment.status === 'APPROVED' || payment.status === 'ALREADY_PAID') {
    return (
      <p style={{ ...noticeStyle, ...paidStyle }}>{t('commerce.confirmation.paymentPaid')}</p>
    );
  }

  // PENDING after the polls ran out, or FAILED. Both end in the same offer,
  // because from the shopper's chair they are the same problem: the order is
  // theirs, it is not paid for, and they would like to fix that.
  return (
    <div style={{ ...noticeStyle, ...unpaidStyle }} role="alert">
      <p style={{ margin: 0 }}>
        {payment.status === 'FAILED'
          ? payment.message || t('commerce.confirmation.paymentFailed')
          : t('commerce.confirmation.paymentUnconfirmed')}
      </p>
      {canRetry && (
        <button type="button" onClick={onRetry} disabled={retrying} style={retryStyle}>
          {retrying ? t('commerce.confirmation.paying') : t('commerce.confirmation.payAgain')}
        </button>
      )}
    </div>
  );
}

const titleStyle: CSSProperties = { margin: '0 0 16px', fontSize: 'var(--font-size-3xl, 30px)' };
const leadStyle: CSSProperties = { margin: '0 0 8px', fontSize: 18 };
const mutedTextStyle: CSSProperties = {
  margin: '0 0 8px',
  color: 'var(--color-text-secondary, #6b7280)',
};
const noticeStyle: CSSProperties = {
  margin: '16px 0 0',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f3f4f6)',
  display: 'grid',
  gap: 8,
  justifyItems: 'start',
};
const paidStyle: CSSProperties = {
  background: 'var(--color-success-50, #ecfdf5)',
  color: 'var(--color-success-700, #047857)',
};
const unpaidStyle: CSSProperties = {
  background: 'var(--color-warning-50, #fffbeb)',
  color: 'var(--color-warning-800, #92400e)',
};
const retryStyle: CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
};
const buttonStyle: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text, #111827)',
  textDecoration: 'none',
  border: '1px solid var(--color-border, #e5e7eb)',
};
